'use client';

import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { $ModalStore, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { getAttachmentAnnotation } from '@nx-frontend/shared/api/webnotes';
import { PopupPayload, PopupType, TextFromImagePayload } from '@nx-frontend/shared/types';

const getTextFromImagePayload = (payload: PopupPayload | undefined): TextFromImagePayload | null => {
  if (payload && payload.payloadType === PopupType.TEXT_FROM_IMAGE) {
    return payload;
  }
  return null;
};

export const TextFromImagePopup: FC = () => {
  const [store, closeModal] = useUnit([$ModalStore, closeModalEvent]);

  const modal = store.find((modal) => modal.payload && modal.payload.payloadType === PopupType.TEXT_FROM_IMAGE);
  const payload = modal ? getTextFromImagePayload(modal.payload) : null;
  const textRef = useRef<HTMLTextAreaElement>(null);

  const [text, setText] = useState<string | null>(null);
  const [showGetError, setShowGetError] = useState<boolean>(false);

  const { toast } = useToast();

  const getAttachmentAnnotationCb = useCallback(async () => {
    if (payload) {
      const annotation = await getAttachmentAnnotation({
        workspaceId: payload.workspaceId,
        attachmentId: payload.attachmentId,
      });
      if (annotation && typeof annotation.text === 'string') {
        setText(annotation.text);
      } else {
        setShowGetError(true);
      }
    }
  }, [payload]);

  useEffect(() => {
    getAttachmentAnnotationCb();
  }, [getAttachmentAnnotationCb]);

  const onTryAgainClick = useCallback(() => {
    setShowGetError(false);
    getAttachmentAnnotationCb();
  }, [getAttachmentAnnotationCb]);

  const onCopyClick = useCallback(async () => {
    if (typeof text === 'string') {
      try {
        await navigator.clipboard.writeText(text);
        toast({ title: 'Copied!' });
      } catch (err) {
        console.info('Failed to copy: ', err);
        toast({ title: 'Failed to copy', description: 'Unknown error' });
      }
    }
  }, [text, toast]);

  const onConfirmClick = useCallback(() => {
    closeModal();
  }, [closeModal]);

  if (!payload) return null;

  return payload.isFeatureAvailable ? (
    <div
      className={classNames(
        {
          'w-[460px] items-start': !showGetError,
          'items-center': showGetError,
        },
        'flex flex-col'
      )}
    >
      {!text ? (
        !showGetError ? (
          <div className="flex items-center justify-center basis-full w-full py-4">Loading...</div>
        ) : (
          <>
            <div className="flex items-center justify-center basis-full w-full py-4 max-w-[550px] text-sm">
              The text still cannot be found. Reason: Image is still processing on the server or no text is found for
              recognition.
            </div>
            <Button type={ButtonType.FLAT} onClick={onTryAgainClick} size={ButtonSize.SM} className="!px-3">
              Try again
            </Button>
          </>
        )
      ) : (
        <>
          <div className="p-4 border-neutral-200 rounded basis-full border w-full mb-2">
            <textarea
              ref={textRef}
              className="w-full resize-none text-sm !border-none !outline-none h-[240px]"
              value={text}
              readOnly={true}
            ></textarea>
          </div>
          <Button type={ButtonType.BASIC} size={ButtonSize.SM} className="!px-0" onClick={onCopyClick}>
            Copy all text
          </Button>
        </>
      )}
    </div>
  ) : (
    <div className="flex flex-col items-end">
      <div className="flex items-center justify-center basis-full w-full py-4 text-sm max-w-[500px]">
        OCR is available only to users of our advanced plan. Contact the organization owner for more details.
      </div>
      <Button type={ButtonType.FLAT} onClick={onConfirmClick} size={ButtonSize.SM} className="!px-3">
        OK
      </Button>
    </div>
  );
};
